import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, LinearProgress, MenuItem, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import {
  acceptSpaWaitlistOffer, cancelSpaWaitlistRequest, createSpaWaitlistRequest,
  declineSpaWaitlistOffer, getSpaCabins, getSpaEquipment, getSpaOperators,
  getSpaTreatments, getSpaWaitlist, getSpaWaitlistOffers, updateSpaWaitlistRequest,
} from '../APIs/SpaAPI';
import { getAllClienti } from '../APIs/ClientsAPI';
import SidebarLayout from '../components/sidebarLayout';
import { usePermissions } from '../contexts/PermissionsContext';
import { useSnackbar } from '../contexts/SnackBarContext';
import { buildWaitlistPayload, offerSecondsRemaining, toWaitlistDraft } from '../utils/spaWaitlist';
import { localizedErrorMessage } from '../utils/italianErrorMessage';

const personName = (person) => [person?.name, person?.surname].filter(Boolean).join(' ') || person?.email || `#${person?.id ?? '-'}`;
const statusColors = { ACTIVE: 'info', CANCELLED: 'default', FULFILLED: 'success' };
const emptyLists = { clients: [], treatments: [], operators: [], cabins: [], equipment: [] };

export default function SpaWaitlistPage() {
  const { t, i18n } = useTranslation();
  const wt = useCallback((key, options = {}) => t(`spa.waitlist.${key}`, options), [t]);
  const { can, loading: permissionsLoading } = usePermissions();
  const { showSnackbar } = useSnackbar();
  const canView = can('spa-appointments:view');
  const canManage = can('spa-appointments:manage');
  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');
  const [requests, setRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [lists, setLists] = useState(emptyLists);
  const [lookupErrors, setLookupErrors] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [lookupReload, setLookupReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [filter, setFilter] = useState('ACTIVE');
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState(null);
  const [draft, setDraft] = useState(toWaitlistDraft());
  const [formError, setFormError] = useState('');
  const [decision, setDecision] = useState(null);
  const [customerType, setCustomerType] = useState('');
  const [commercialStatus, setCommercialStatus] = useState('CONFIRMED');
  const [createdAppointment, setCreatedAppointment] = useState(null);
  const version = useRef(0);
  const mounted = useRef(false);
  const busyRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!canView || !token || !tenant) return;
    const current = ++version.current;
    setLoading(true);
    const results = await Promise.allSettled([
      getSpaWaitlist({}, token, tenant),
      getSpaWaitlistOffers({ status: 'ACTIVE' }, token, tenant),
    ]);
    if (!mounted.current || current !== version.current) return;
    const nextErrors = {};
    results.forEach((result, index) => {
      const key = index ? 'offers' : 'requests';
      if (result.status === 'fulfilled' && Array.isArray(result.value)) {
        (index ? setOffers : setRequests)(result.value);
      } else nextErrors[key] = result.reason?.message || wt('loadFailed');
    });
    setErrors(nextErrors);
    setLoading(false);
  }, [canView, token, tenant, wt]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const polling = setInterval(() => {
      if (!busyRef.current && document.visibilityState !== 'hidden') refresh();
    }, 15000);
    const focus = () => { if (!busyRef.current) refresh(); };
    window.addEventListener('focus', focus);
    window.addEventListener('online', focus);
    return () => {
      mounted.current = false;
      version.current += 1;
      clearInterval(polling);
      window.removeEventListener('focus', focus);
      window.removeEventListener('online', focus);
    };
  }, [refresh]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!canView || !token || !tenant) return undefined;
    let disposed = false;
    setLoadingLookups(true);
    const keys = ['clients', 'treatments', 'operators', 'cabins', 'equipment'];
    Promise.allSettled([
      getAllClienti(token, tenant, { noCache: true }), getSpaTreatments(token, tenant),
      getSpaOperators(token, tenant), getSpaCabins(token, tenant), getSpaEquipment(token, tenant),
    ]).then((results) => {
      if (disposed) return;
      const next = { ...emptyLists };
      const failed = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && Array.isArray(result.value)) next[keys[index]] = result.value;
        else failed.push(keys[index]);
      });
      setLists(next);
      setLookupErrors(failed);
      setLoadingLookups(false);
    });
    return () => { disposed = true; };
  }, [canView, token, tenant, lookupReload]);

  const dateTime = (value) => {
    const date = new Date(value);
    return value && Number.isFinite(date.getTime()) ? date.toLocaleString(i18n.language, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }) : '-';
  };
  const liveOffers = offers.filter((offer) => offer.status === 'ACTIVE' && offerSecondsRemaining(offer.expiresAt, now) > 0);
  const offeredIds = new Set(liveOffers.map((offer) => offer.request.id));
  const visibleRequests = useMemo(() => requests.filter((request) => (
    (!filter || request.status === filter)
    && [personName(request.client), request.client?.email, request.client?.phone, request.treatment?.name, request.id]
      .filter(Boolean).join(' ').toLowerCase().includes(search.trim().toLowerCase())
  )), [requests, filter, search]);
  const resourceNames = (offer) => {
    const resources = offer.resources || {};
    const items = [];
    (resources.operatorIds || []).forEach((id) => items.push(lists.operators.find((item) => item.id === id)?.displayName || wt('operatorNumber', { id })));
    (resources.cabinIds || []).forEach((id) => items.push(lists.cabins.find((item) => item.id === id)?.name || wt('cabinNumber', { id })));
    (resources.equipment || []).forEach((item) => items.push(`${lists.equipment.find((entry) => entry.id === item.equipmentId)?.name || wt('equipmentNumber', { id: item.equipmentId })} x${item.quantity}`));
    return items.join(', ') || '-';
  };
  const openEditor = (request = null) => {
    setEditor({ id: request?.id });
    setDraft(toWaitlistDraft(request));
    setFormError('');
  };
  const openDecision = (kind, item) => {
    setDecision({ kind, item });
    setCustomerType('');
    setCommercialStatus('CONFIRMED');
    setFormError('');
  };
  const lock = () => {
    if (!canManage || busyRef.current) return false;
    busyRef.current = true;
    version.current += 1;
    setBusy(true);
    setLoading(false);
    setFormError('');
    return true;
  };
  const unlock = () => { busyRef.current = false; if (mounted.current) setBusy(false); };
  const saveRequest = async (event) => {
    event.preventDefault();
    let payload;
    try { payload = buildWaitlistPayload(draft); }
    catch (error) { setFormError(wt(error.message)); return; }
    if (!lock()) return;
    try {
      const saved = editor.id
        ? await updateSpaWaitlistRequest(editor.id, payload, token, tenant)
        : await createSpaWaitlistRequest(payload, token, tenant);
      if (!mounted.current) return;
      setRequests((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      setEditor(null);
      showSnackbar(wt(editor.id ? 'updated' : 'created'), 'success');
      await refresh();
    } catch (error) {
      if (mounted.current) {
        setFormError(localizedErrorMessage(error, wt('actionFailed')));
        if (error.status === 409 || error.status === 404) await refresh();
      }
    } finally { unlock(); }
  };
  const confirmDecision = async () => {
    if (!decision || (decision.kind !== 'cancel' && offerSecondsRemaining(decision.item.expiresAt) <= 0)
      || (decision.kind === 'cancel' ? errors.requests : errors.offers)
      || (decision.kind === 'accept' && !customerType)) return;
    if (!lock()) return;
    try {
      const { kind, item } = decision;
      if (kind === 'accept') {
        const result = await acceptSpaWaitlistOffer(item.id, { customerType, commercialStatus }, token, tenant);
        if (!mounted.current) return;
        setCreatedAppointment({ id: result.appointment.id, startAt: result.appointment.startAt || item.startAt });
        setOffers((current) => current.filter((offer) => offer.id !== item.id));
        setRequests((current) => current.map((request) => request.id === item.request.id ? result.offer.request : request));
      } else if (kind === 'decline') {
        const result = await declineSpaWaitlistOffer(item.id, token, tenant);
        if (!mounted.current) return;
        setOffers((current) => [...current.filter((offer) => offer.id !== item.id), ...(result.nextOffer ? [result.nextOffer] : [])]);
      } else {
        const cancelled = await cancelSpaWaitlistRequest(item.id, token, tenant);
        if (!mounted.current) return;
        setRequests((current) => current.map((request) => request.id === item.id ? cancelled : request));
        setOffers((current) => current.filter((offer) => offer.request.id !== item.id));
      }
      setDecision(null);
      showSnackbar(wt(`${kind}Success`), 'success');
      await refresh();
    } catch (error) {
      if (mounted.current) {
        setFormError(localizedErrorMessage(error, wt('actionFailed')));
        if (error.status === 409 || error.status === 404) await refresh();
      }
    } finally { unlock(); }
  };
  const changeDraft = (key) => (event) => setDraft((current) => ({ ...current, [key]: event.target.value }));
  const lookupField = (key, collection, label, name) => (
    lookupErrors.includes(collection) ? (
      <TextField label={wt(`${label}Id`)} type="number" value={draft[key]} onChange={changeDraft(key)}
        required={key !== 'preferredOperatorId'} inputProps={{ min: 1, step: 1 }} />
    ) : (
      <Autocomplete options={lists[collection].filter((item) => item.active !== false || String(item.id) === String(draft[key]))}
        value={lists[collection].find((item) => String(item.id) === String(draft[key])) || null}
        getOptionLabel={(item) => `${name(item)} (#${item.id})`}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_, item) => setDraft((current) => ({ ...current, [key]: item?.id || '' }))}
        renderInput={(params) => <TextField {...params} label={wt(label)} required={key !== 'preferredOperatorId'} />}
      />
    )
  );
  const expiredDecision = decision && decision.kind !== 'cancel' && offerSecondsRemaining(decision.item.expiresAt, now) <= 0;
  const unavailableDecision = decision && decision.kind !== 'cancel' && !liveOffers.some((offer) => offer.id === decision.item.id);

  return (
    <SidebarLayout className="admin-responsive-page">
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1320, mx: 'auto' }}>
        {permissionsLoading ? <CircularProgress aria-label={wt('loading')} /> : !canView ? (
          <Alert severity="warning">{wt('noPermission')}</Alert>
        ) : (
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
              <Typography variant="h4" fontWeight={800}>{wt('title')}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title={wt('refresh')}><span><IconButton aria-label={wt('refresh')} disabled={loading || busy} onClick={() => { refresh(); setLookupReload((current) => current + 1); }}><RefreshIcon /></IconButton></span></Tooltip>
                <Button variant="contained" startIcon={<AddIcon />} disabled={!canManage || busy || loadingLookups} onClick={() => openEditor()}>{wt('newRequest')}</Button>
              </Stack>
            </Stack>
            {!canManage && <Alert severity="info">{wt('readOnly')}</Alert>}
            {lookupErrors.length > 0 && <Alert severity="warning">{wt('lookupsUnavailable', { lists: lookupErrors.map((key) => wt(key)).join(', ') })}</Alert>}
            {!loadingLookups && !lookupErrors.includes('treatments') && !lists.treatments.some((item) => item.active !== false) && <Alert severity="info">
              {wt('noTreatments')}{' '}
              {can('spa-catalogs:view') && <Button component={Link} size="small" to="/spa/catalog">{wt('openCatalog')}</Button>}
            </Alert>}
            {createdAppointment && <Alert severity="success" onClose={() => setCreatedAppointment(null)}>
              {wt('appointmentCreated', { id: createdAppointment.id })}{' '}
              <Button component={Link} size="small" to={`/spa/planner?date=${toWaitlistDraft({ desiredStartAt: createdAppointment.startAt }).desiredStartAt.slice(0, 10)}`}>{wt('openPlanner')}</Button>
            </Alert>}
            {loading && <LinearProgress aria-label={wt('loading')} />}

            <Box component="section">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="h6" fontWeight={700}>{wt('slotOffers')}</Typography>
                <Chip size="small" color="warning" label={liveOffers.length} />
              </Stack>
              {errors.offers && <Alert severity="error" sx={{ mb: 1 }}>{wt('offersLoadFailed')} {errors.offers}</Alert>}
              <Stack spacing={1}>
                {!offers.length && !loading && !errors.offers && <Typography color="text.secondary">{wt('noOffers')}</Typography>}
                {offers.map((offer) => {
                  const seconds = offerSecondsRemaining(offer.expiresAt, now);
                  return (
                    <Box key={offer.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <Typography fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>{personName(offer.request.client)}</Typography>
                          <Typography variant="body2">{offer.request.treatment.name} | {wt('party', { count: offer.request.partySize })}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{[offer.request.client.phone, offer.request.client.email].filter(Boolean).join(' | ')}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2">{dateTime(offer.startAt)} - {dateTime(offer.endAt)}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{resourceNames(offer)}</Typography>
                          <Typography variant="caption" color="text.secondary">{wt('expiresAt', { date: dateTime(offer.expiresAt) })}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" justifyContent={{ md: 'flex-end' }}>
                            <Chip size="small" color={seconds ? 'warning' : 'default'} sx={{ minWidth: 70 }} label={seconds ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : wt('expired')} />
                            <Button size="small" startIcon={<CloseIcon />} disabled={!canManage || busy || !seconds || Boolean(errors.offers)} onClick={() => openDecision('decline', offer)}>{wt('decline')}</Button>
                            <Button size="small" variant="contained" startIcon={<CheckIcon />} disabled={!canManage || busy || !seconds || Boolean(errors.offers)} onClick={() => openDecision('accept', offer)}>{wt('accept')}</Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <Box component="section">
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>{wt('requests')}</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField size="small" label={wt('search')} value={search} onChange={(event) => setSearch(event.target.value)} />
                  <TextField select size="small" label={wt('status')} value={filter} onChange={(event) => setFilter(event.target.value)} sx={{ minWidth: 155 }}>
                    <MenuItem value="">{wt('all')}</MenuItem>
                    {['ACTIVE', 'CANCELLED', 'FULFILLED'].map((status) => <MenuItem key={status} value={status}>{wt(`statuses.${status}`)}</MenuItem>)}
                  </TextField>
                </Stack>
              </Stack>
              {errors.requests && <Alert severity="error" sx={{ mb: 1 }}>{wt('requestsLoadFailed')} {errors.requests}</Alert>}
              {!visibleRequests.length && !loading && !errors.requests && <Typography color="text.secondary">{wt('noRequests')}</Typography>}
              <Stack spacing={1}>
                {visibleRequests.map((request) => (
                  <Box key={request.id} sx={{ borderBottom: 1, borderColor: 'divider', py: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <Typography fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>{personName(request.client)}</Typography>
                        <Typography variant="caption" color="text.secondary">#{request.id} | {dateTime(request.createdAt)}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2">{request.treatment.name} | {wt('party', { count: request.partySize })}</Typography>
                        <Typography variant="body2" color="text.secondary">{request.preferredOperator ? personName(request.preferredOperator) : wt('anyOperator')}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}><Typography variant="body2">{dateTime(request.desiredStartAt)} - {dateTime(request.desiredEndAt)}</Typography></Grid>
                      <Grid item xs={12} md={3}>
                        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" justifyContent={{ md: 'flex-end' }}>
                          <Chip size="small" color={statusColors[request.status] || 'default'} label={wt(`statuses.${request.status}`)} />
                          {offeredIds.has(request.id) && <Chip size="small" color="warning" label={wt('offered')} />}
                          <Tooltip title={offeredIds.has(request.id) ? wt('cannotEditOffered') : wt('edit')}><span>
                            <IconButton aria-label={`${wt('edit')} #${request.id}`} size="small" disabled={!canManage || busy || request.status !== 'ACTIVE' || offeredIds.has(request.id) || Boolean(errors.requests || errors.offers)} onClick={() => openEditor(request)}><EditIcon /></IconButton>
                          </span></Tooltip>
                          <Tooltip title={wt('cancelRequest')}><span>
                            <IconButton aria-label={`${wt('cancelRequest')} #${request.id}`} size="small" disabled={!canManage || busy || request.status !== 'ACTIVE' || Boolean(errors.requests)} onClick={() => openDecision('cancel', request)}><CloseIcon /></IconButton>
                          </span></Tooltip>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>

      <Dialog open={Boolean(editor)} onClose={() => { if (!busy) setEditor(null); }} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={saveRequest}>
          <DialogTitle>{wt(editor?.id ? 'editRequest' : 'newRequest')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              {lookupErrors.length > 0 && <Alert severity="warning">{wt('knownIds')}</Alert>}
              <Box component="fieldset" disabled={busy || !canManage} sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
                <Stack spacing={2}>
                  {lookupField('clientId', 'clients', 'client', personName)}
                  {lookupField('treatmentProductId', 'treatments', 'treatment', (item) => item.name)}
                  <TextField type="number" label={wt('partySize')} required value={draft.partySize} onChange={changeDraft('partySize')} inputProps={{ min: 1, max: 100, step: 1 }} />
                  <TextField type="datetime-local" label={wt('from')} required InputLabelProps={{ shrink: true }} value={draft.desiredStartAt} onChange={changeDraft('desiredStartAt')} />
                  <TextField type="datetime-local" label={wt('to')} required InputLabelProps={{ shrink: true }} value={draft.desiredEndAt} onChange={changeDraft('desiredEndAt')} />
                  {lookupField('preferredOperatorId', 'operators', 'preferredOperator', (item) => item.displayName || personName(item))}
                </Stack>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setEditor(null)}>{wt('close')}</Button>
            <Button type="submit" variant="contained" disabled={busy || !canManage} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>{wt('save')}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(decision)} onClose={() => { if (!busy) setDecision(null); }} fullWidth maxWidth="sm">
        <DialogTitle>{wt(`${decision?.kind || 'accept'}Title`)}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <Typography fontWeight={700}>{personName(decision?.item.request?.client || decision?.item.client)}</Typography>
            {decision?.kind !== 'cancel' && decision && <Typography>{dateTime(decision.item.startAt)} - {dateTime(decision.item.endAt)}</Typography>}
            <Typography color="text.secondary">{wt(`${decision?.kind || 'accept'}Description`)}</Typography>
            {(expiredDecision || unavailableDecision) && <Alert severity="warning">{wt('offerUnavailable')}</Alert>}
            {decision?.kind === 'accept' && <>
              <TextField select label={wt('customerType')} value={customerType} onChange={(event) => setCustomerType(event.target.value)} disabled={busy} required>
                {['HOTEL_GUEST', 'EXTERNAL_GUEST', 'MEMBER'].map((type) => <MenuItem key={type} value={type}>{wt(`customerTypes.${type}`)}</MenuItem>)}
              </TextField>
              <TextField select label={wt('commercialStatus')} value={commercialStatus} onChange={(event) => setCommercialStatus(event.target.value)} disabled={busy}>
                {['CONFIRMED', 'GUARANTEED'].map((status) => <MenuItem key={status} value={status}>{wt(`commercialStatuses.${status}`)}</MenuItem>)}
              </TextField>
            </>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setDecision(null)}>{wt('close')}</Button>
          <Button variant="contained" color={decision?.kind === 'cancel' ? 'error' : 'primary'}
            disabled={busy || !canManage || expiredDecision || unavailableDecision || Boolean(decision?.kind === 'cancel' ? errors.requests : errors.offers) || (decision?.kind === 'accept' && !customerType)}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />} onClick={confirmDecision}>{wt('confirm')}</Button>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
}
